// src/components/notifications/NotificationsProvider.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, X } from "lucide-react";
import { useProductStore } from "../../store/useProductStore";

/* ------------------------ Generator registry (routes + media) ------------------------ */
const GENERATORS = {
  // Ads
  "product-avatar-video": {
    title: "Product Avatar Video",
    routeBase: "/generate/product-avatar-video",
    media: { type: "video", src: "/media/product-avatar-video.mp4" },
  },
  "ad-marketing-video": {
    title: "Avatar Explainer Video",
    routeBase: "/generate/ad-marketing-video",
    media: { type: "video", src: "/media/avatar-explainer-video.mp4" },
  },
  "product-avatar-image": {
    title: "Product Avatar Image",
    routeBase: "/generate/product-avatar-image",
    media: { type: "image", src: "/media/product-avatar-image.webp" },
  },
  "photoshoot-anyshoot": {
    title: "Product Photoshoot",
    routeBase: "/generate/photoshoot-anyshoot",
    media: { type: "video", src: "/media/product-photoshoot.mp4" },
  },
  "background-replacer": {
    title: "Product Background Changer",
    routeBase: "/generate/background-replacer",
    media: { type: "video", src: "/media/product-background-changer.mp4" },
  },
  "ad-creative": {
    title: "Ad Creative Image",
    routeBase: "/generate/ad-creatives",
    media: { type: "video", src: "/media/ad-creative-image.mp4" },
  },
  "ad-creatives": {
    title: "Ad Creative Image",
    routeBase: "/generate/ad-creatives",
    media: { type: "video", src: "/media/ad-creative-image.mp4" },
  },

  // Improvements
  "remove-bg": {
    title: "Remove Image Background",
    routeBase: "/generate/remove-bg",
    media: { type: "video", src: "/media/remove-image-background.mp4" },
  },
  "color-restoration": {
    title: "Color Restoration",
    routeBase: "/generate/color-restoration",
    media: { type: "video", src: "/media/color-restoration.mp4" },
  },
  enhancer: {
    title: "Enhancer",
    routeBase: "/generate/enhancer",
    media: { type: "video", src: "/media/enhancer.mp4" },
  },
  outpaint: {
    title: "Extend Image (Outpaint)",
    routeBase: "/generate/outpaint",
    media: { type: "video", src: "/media/extend-image-outpaint.mp4" },
  },
  "light-fix": {
    title: "Light Fix",
    routeBase: "/generate/light-fix",
    media: { type: "video", src: "/media/light-fix.mp4" },
  },
  "object-replacer": {
    title: "Object Replacer",
    routeBase: "/generate/object-replacer",
    media: { type: "video", src: "/media/object-replacer.mp4" },
  },
  "object-remover": {
    title: "Object Remover",
    routeBase: "/generate/object-remover",
    media: { type: "video", src: "/media/object-remover.mp4" },
  },
  "old-photos": {
    title: "Old Photos Restoration",
    routeBase: "/generate/old-photos-restoration",
    media: { type: "video", src: "/media/old-photos-restoration.mp4" },
  },
  "old-photos-restoration": {
    title: "Old Photos Restoration",
    routeBase: "/generate/old-photos-restoration",
    media: { type: "video", src: "/media/old-photos-restoration.mp4" },
  },
  upscaler: {
    title: "Upscaler",
    routeBase: "/generate/upscaler",
    media: { type: "video", src: "/media/upscaler.mp4" },
  },

  // Aliases (camelCase)
  productAvatarVideo: {
    title: "Product Avatar Video",
    routeBase: "/generate/product-avatar-video",
    media: { type: "video", src: "/media/product-avatar-video.mp4" },
  },
  adMarketingVideo: {
    title: "Avatar Explainer Video",
    routeBase: "/generate/ad-marketing-video",
    media: { type: "video", src: "/media/avatar-explainer-video.mp4" },
  },
  productAvatarImage: {
    title: "Product Avatar Image",
    routeBase: "/generate/product-avatar-image",
    media: { type: "image", src: "/media/product-avatar-image.webp" },
  },
  photoshootAnyshoot: {
    title: "Product Photoshoot",
    routeBase: "/generate/photoshoot-anyshoot",
    media: { type: "video", src: "/media/product-photoshoot.mp4" },
  },
  photoshoot: {
    title: "Product Background Changer",
    routeBase: "/generate/background-replacer",
    media: { type: "video", src: "/media/product-background-changer.mp4" },
  },
  adCreatives: {
    title: "Ad Creative Image",
    routeBase: "/generate/ad-creatives",
    media: { type: "video", src: "/media/ad-creative-image.mp4" },
  },
  removeBackground: {
    title: "Remove Image Background",
    routeBase: "/generate/remove-bg",
    media: { type: "video", src: "/media/remove-image-background.mp4" },
  },
  lightFix: {
    title: "Light Fix",
    routeBase: "/generate/light-fix",
    media: { type: "video", src: "/media/light-fix.mp4" },
  },
  objectReplacer: {
    title: "Object Replacer",
    routeBase: "/generate/object-replacer",
    media: { type: "video", src: "/media/object-replacer.mp4" },
  },
  objectRemover: {
    title: "Object Remover",
    routeBase: "/generate/object-remover",
    media: { type: "video", src: "/media/object-remover.mp4" },
  },
  oldPhotosRestoration: {
    title: "Old Photos Restoration",
    routeBase: "/generate/old-photos-restoration",
    media: { type: "video", src: "/media/old-photos-restoration.mp4" },
  },
};

function normalizeFeatureKey(feature) {
  if (!feature) return "";
  if (GENERATORS[feature]) return feature;

  const raw = String(feature);
  const kebab = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .toLowerCase();

  if (GENERATORS[kebab]) return kebab;
  if (GENERATORS[raw]) return raw;

  return kebab;
}

function getGeneratorInfo(feature) {
  const key = normalizeFeatureKey(feature);
  return GENERATORS[key] || null;
}

function formatCreatedAt(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeAssetTypeToMediaType(assetType) {
  const t = String(assetType || "").toLowerCase();
  if (!t) return null;
  if (t === "video") return "video";
  if (t === "photo" || t === "image") return "image";
  return null;
}

function guessMediaTypeFromUrl(url) {
  const u = String(url || "").toLowerCase();
  if (!u) return null;

  if (
    u.endsWith(".mp4") ||
    u.endsWith(".mov") ||
    u.endsWith(".webm") ||
    u.includes("resource_type=video") ||
    u.includes("/video/upload/")
  ) {
    return "video";
  }

  if (
    u.endsWith(".png") ||
    u.endsWith(".jpg") ||
    u.endsWith(".jpeg") ||
    u.endsWith(".webp") ||
    u.endsWith(".gif") ||
    u.includes("/image/upload/")
  ) {
    return "image";
  }

  return "image";
}

function extractActualAssetMedia(n) {
  const topUrl = n?.asset_url || n?.assetUrl || "";
  if (topUrl && topUrl !== "/spinner.gif") {
    const t =
      normalizeAssetTypeToMediaType(n?.asset_type || n?.assetType) ||
      guessMediaTypeFromUrl(topUrl);
    return { type: t === "video" ? "video" : "image", src: topUrl };
  }

  const d = n?.data || {};
  const src =
    d.asset_url ||
    d.assetUrl ||
    d.url ||
    d.output_url ||
    d.outputUrl ||
    d.result_url ||
    d.resultUrl ||
    d.media_url ||
    d.mediaUrl ||
    d.asset?.url ||
    "";

  if (!src || src === "/spinner.gif") return null;

  const forcedType =
    d.asset_type || d.assetType || d.media_type || d.mediaType || d.type;
  const type = forcedType
    ? String(forcedType).toLowerCase()
    : guessMediaTypeFromUrl(src);

  if (type === "video") return { type: "video", src };
  return { type: "image", src };
}

/* ----------------------------- Context ---------------------------- */
const NotificationsContext = createContext(null);

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx)
    throw new Error("useNotifications must be used inside NotificationsProvider");
  return ctx;
}

function normalizeItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter((n) => n && n.id && n.created_at)
    .map((n) => ({
      id: String(n.id),
      type: String(n.type || "info"),
      title: String(n.title || "Notification"),
      message: String(n.message || ""),
      data: n.data || {},
      is_read: Boolean(n.is_read),
      created_at: String(n.created_at),

      clicked_at: n.clicked_at ? String(n.clicked_at) : null,
      click_count: Number.isFinite(Number(n.click_count)) ? Number(n.click_count) : 0,

      asset_url: n.asset_url ? String(n.asset_url) : "",
      asset_type: n.asset_type ? String(n.asset_type) : "",
    }));
}

function isToastType(t) {
  return t === "asset_success" || t === "asset_failed";
}

function buildNotificationCopy(n) {
  const gen = getGeneratorInfo(n?.data?.feature);
  const generatorTitle = gen?.title || "Generator";

  const isSuccess = n.type === "asset_success";
  const statusWord = isSuccess ? "ready" : "failed";

  const title = `Asset from ${generatorTitle} ${statusWord}`;
  const createdText = formatCreatedAt(n.created_at);

  const message = isSuccess
    ? "Your asset has been generated successfully."
    : "Something went wrong while generating your asset.";

  const actual = extractActualAssetMedia(n);
  const previewMedia = actual || gen?.media || null;

  return { title, message, generatorTitle, createdText, gen, previewMedia };
}

function ToastPreview({ media }) {
  if (!media?.src) {
    return (
      <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center" />
    );
  }

  if (media.type === "image") {
    return (
      <img
        src={media.src}
        alt="Preview"
        className="w-11 h-11 rounded-xl object-cover border border-white/10"
        draggable={false}
        loading="lazy"
      />
    );
  }

  return (
    <video
      src={media.src}
      className="w-11 h-11 rounded-xl object-cover border border-white/10"
      muted
      loop
      playsInline
      autoPlay
      preload="metadata"
    />
  );
}

function Toast({ n, onDismiss, onOpen }) {
  const isSuccess = n.type === "asset_success";
  const { title, message, createdText, previewMedia } = buildNotificationCopy(n);

  const icon = isSuccess ? (
    <CheckCircle2 size={18} className="text-[#4ade80]" />
  ) : (
    <AlertTriangle size={18} className="text-[#f87171]" />
  );

  return (
    <div className="w-[420px] max-w-[calc(100vw-2rem)] rounded-2xl bg-[#141428] border border-[#2A2A40] shadow-2xl shadow-black/40 overflow-hidden">
      <div className="h-[3px] bg-white/10">
        <div
          className="h-[3px] bg-[#FC2783] animate-[notifbar_5s_linear_forwards]"
          aria-hidden="true"
        />
      </div>

      <div className="p-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => onOpen(n)}
            className="text-left flex items-start gap-3 min-w-0 flex-1"
            aria-label="Open notification"
          >
            <ToastPreview media={previewMedia} />

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="mt-[2px]">{icon}</span>
                  <div className="text-sm font-semibold text-white truncate">
                    {title}
                  </div>
                </div>
              </div>

              <div className="text-[12px] text-white/70 leading-5 mt-1">
                {message}
              </div>

              {createdText ? (
                <div className="text-[11px] text-white/45 mt-1.5">
                  {createdText}
                </div>
              ) : null}
            </div>
          </button>

          <button
            type="button"
            onClick={() => onDismiss(n.id)}
            className="p-1 rounded-lg hover:bg-white/5 text-white/70 hover:text-white"
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes notifbar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}

export function NotificationsProvider({ apiBase, children }) {
  const { user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const { setSelectedProduct } = useProductStore();

  const API_BASE = apiBase;

  const [unreadCount, setUnreadCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);

  const [timeline, setTimeline] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const [toasts, setToasts] = useState([]);

  const lastSinceRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const pollTimerRef = useRef(null);

  // ✅ prevent overlapping polls (sleep/wake can cause bursts)
  const pollInFlightRef = useRef(false);

  const canPollNow = useCallback(() => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      return false;
    }
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return false;
    }
    return true;
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    if (!canPollNow()) return;

    try {
      const token = await getToken().catch(() => null);
      if (!token) return;

      const res = await axios.get(`${API_BASE}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      setUnreadCount(Number(res?.data?.count || 0));
    } catch {
      // ignore network/token hiccups (sleep/wake)
    }
  }, [API_BASE, canPollNow, getToken, user]);

  const markRead = useCallback(
    async (id) => {
      if (!id || !user) return;
      if (!canPollNow()) return;

      try {
        const token = await getToken().catch(() => null);
        if (!token) return;

        await axios.post(
          `${API_BASE}/api/notifications/${id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );

        setTimeline((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    },
    [API_BASE, canPollNow, getToken, user]
  );

  const markClicked = useCallback(
    async (id) => {
      if (!id || !user) return;
      if (!canPollNow()) return;

      try {
        const token = await getToken().catch(() => null);
        if (!token) return;

        const res = await axios.post(
          `${API_BASE}/api/notifications/${id}/click`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );

        const clickedAt = res?.data?.clicked_at
          ? String(res.data.clicked_at)
          : new Date().toISOString();
        const clickCount = Number.isFinite(Number(res?.data?.click_count))
          ? Number(res.data.click_count)
          : 1;

        setTimeline((prev) =>
          prev.map((n) =>
            n.id === id
              ? {
                  ...n,
                  is_read: true,
                  clicked_at: clickedAt,
                  click_count: clickCount,
                }
              : n
          )
        );

        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    },
    [API_BASE, canPollNow, getToken, user]
  );

  const dismissNotification = useCallback(
    async (id) => {
      if (!id || !user) return;
      if (!canPollNow()) return;

      try {
        const token = await getToken().catch(() => null);
        if (!token) return;

        await axios.post(
          `${API_BASE}/api/notifications/${id}/dismiss`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );

        setTimeline((prev) => prev.filter((n) => n.id !== id));
        setToasts((prev) => prev.filter((n) => n.id !== id));
      } catch {
        // ignore
      } finally {
        fetchUnreadCount();
      }
    },
    [API_BASE, canPollNow, fetchUnreadCount, getToken, user]
  );

  const resolveNotificationTarget = useCallback((n) => {
    const projectId =
      n?.data?.project_id || n?.data?.projectId || n?.data?.projectID;
    const feature = n?.data?.feature;

    if (!projectId) return null;

    const gen = getGeneratorInfo(feature);
    if (gen?.routeBase) return `${gen.routeBase}/${projectId}`;

    return `/project/${projectId}`;
  }, []);

  const openNotification = useCallback(
    (n) => {
      if (!n?.id) return;

      const projectId =
        n?.data?.project_id || n?.data?.projectId || n?.data?.projectID;

      if (projectId) {
        try {
          localStorage.setItem("selectedProductId", String(projectId));
        } catch {
          // ignore
        }

        // placeholder prevents URL ping-pong; Header refresh will hydrate it to the real project
        setSelectedProduct({
          id: String(projectId),
          product_name: "Loading...",
          title: "",
          name: "",
          url: "",
          thumbnail_url: "",
        });

        // ✅ force Header to refetch projects so dropdown shows the real selected product ASAP
        try {
          window.dispatchEvent(new Event("pixelads:projects:refresh"));
        } catch {
          // ignore
        }
      }

      setPanelOpen(false);

      const target = resolveNotificationTarget(n);
      if (target) navigate(target);

      void markClicked(n.id);
    },
    [markClicked, navigate, resolveNotificationTarget, setSelectedProduct]
  );

  const fetchFirstPage = useCallback(async () => {
    if (!user) return;
    if (!canPollNow()) return;

    setTimelineLoading(true);

    try {
      const token = await getToken().catch(() => null);
      if (!token) return;

      const res = await axios.get(`${API_BASE}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const items = normalizeItems(res?.data?.items);
      items.forEach((n) => seenIdsRef.current.add(n.id));

      items.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setTimeline(items);
      setNextCursor(res?.data?.nextCursor || null);
    } catch {
      // ignore
    } finally {
      setTimelineLoading(false);
    }
  }, [API_BASE, canPollNow, getToken, user]);

  const fetchMore = useCallback(async () => {
    if (!user) return;
    if (!nextCursor || timelineLoading) return;
    if (!canPollNow()) return;

    setTimelineLoading(true);
    try {
      const token = await getToken().catch(() => null);
      if (!token) return;

      const res = await axios.get(
        `${API_BASE}/api/notifications?limit=20&cursor=${encodeURIComponent(nextCursor)}`,
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );

      const items = normalizeItems(res?.data?.items);
      items.forEach((n) => seenIdsRef.current.add(n.id));

      setTimeline((prev) => {
        const merged = [...prev, ...items];
        merged.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const out = [];
        const seen = new Set();
        for (const x of merged) {
          if (seen.has(x.id)) continue;
          seen.add(x.id);
          out.push(x);
        }
        return out;
      });

      setNextCursor(res?.data?.nextCursor || null);
    } catch {
      // ignore
    } finally {
      setTimelineLoading(false);
    }
  }, [API_BASE, canPollNow, getToken, nextCursor, timelineLoading, user]);

  useEffect(() => {
    if (!toasts.length) return;

    const timers = toasts.map((t) =>
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x.id !== t.id));
      }, 5000)
    );

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [toasts]);

  const pollNew = useCallback(async () => {
    if (!user) return;
    if (!canPollNow()) return;

    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;

    try {
      const token = await getToken().catch(() => null);
      if (!token) return;

      const since = lastSinceRef.current;
      const url = since
        ? `${API_BASE}/api/notifications?limit=25&since=${encodeURIComponent(since)}`
        : `${API_BASE}/api/notifications?limit=25`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const items = normalizeItems(res?.data?.items);

      // ✅ only advance "since" after a successful fetch
      lastSinceRef.current = new Date().toISOString();

      const fresh = items.filter((x) => !seenIdsRef.current.has(x.id));
      if (!fresh.length) {
        // still refresh count occasionally
        fetchUnreadCount();
        return;
      }

      fresh.forEach((x) => seenIdsRef.current.add(x.id));

      setTimeline((prev) => {
        const merged = [...fresh, ...prev];
        merged.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const out = [];
        const seen = new Set();
        for (const x of merged) {
          if (seen.has(x.id)) continue;
          seen.add(x.id);
          out.push(x);
        }
        return out;
      });

      const toastables = fresh.filter((x) => isToastType(x.type));
      if (toastables.length) {
        setToasts((prev) => {
          const merged = [...toastables, ...prev];
          const out = [];
          const seen = new Set();
          for (const x of merged) {
            if (seen.has(x.id)) continue;
            seen.add(x.id);
            out.push(x);
          }
          return out;
        });
      }

      fetchUnreadCount();
    } catch {
      // ignore network/token hiccups (sleep/wake)
    } finally {
      pollInFlightRef.current = false;
    }
  }, [API_BASE, canPollNow, fetchUnreadCount, getToken, user]);

  useEffect(() => {
    // seed "since" so first poll doesn't pull too far back
    lastSinceRef.current = new Date(Date.now() - 10_000).toISOString();

    fetchUnreadCount();
    pollNew();

    if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
    pollTimerRef.current = window.setInterval(() => {
      pollNew();
    }, 8000);

    const onFocus = () => {
      // quick refresh after sleep/wake
      pollNew();
      fetchUnreadCount();
    };

    const onOnline = () => {
      pollNew();
      fetchUnreadCount();
    };

    const onVis = () => {
      if (document.visibilityState === "visible") {
        pollNew();
        fetchUnreadCount();
      }
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (pollTimerRef.current) window.clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;

      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchUnreadCount, pollNew]);

  useEffect(() => {
    if (!panelOpen) return;
    fetchFirstPage();
  }, [fetchFirstPage, panelOpen]);

  const value = useMemo(
    () => ({
      unreadCount,
      panelOpen,
      setPanelOpen,
      timeline,
      nextCursor,
      timelineLoading,
      fetchMore,
      markRead,
      markClicked,
      dismissNotification,
      fetchFirstPage,
      toasts,
      dismissToast: (id) => setToasts((prev) => prev.filter((x) => x.id !== id)),
      openNotification,
      getGeneratorInfo,
      formatCreatedAt,
      buildNotificationCopy,
    }),
    [
      unreadCount,
      panelOpen,
      timeline,
      nextCursor,
      timelineLoading,
      fetchMore,
      markRead,
      markClicked,
      dismissNotification,
      fetchFirstPage,
      toasts,
      openNotification,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}

      <div className="fixed right-4 top-[84px] z-[60] flex flex-col gap-3">
        {toasts.map((n) => (
          <Toast
            key={n.id}
            n={n}
            onDismiss={dismissNotification}
            onOpen={openNotification}
          />
        ))}
      </div>
    </NotificationsContext.Provider>
  );
}
